import { Checkbox, Spinner, Stack } from 'component-library';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

import { SuspenseBoundary } from '@/components';
import { useSelectedIndicatorProducts } from '@/pages/shared/indicators/hooks/useIndicatorProducts';

import { useWorkstreamCreateContext, WORKSTREAM_FORM_KEYS } from '../hooks/useFormControls';

export const WorkstreamsFormIndicatorProducts = ({ form }: { form: ReturnType<typeof useWorkstreamCreateContext> }) => (
  <ErrorBoundary FallbackComponent={() => null}>
    <SuspenseBoundary
      fallback={
        <Stack centerMain>
          <Spinner size={10} />
        </Stack>
      }
    >
      <WorkstreamsFormIndicatorProductsBase form={form} />
    </SuspenseBoundary>
  </ErrorBoundary>
);

const WorkstreamsFormIndicatorProductsBase = ({ form }: { form: ReturnType<typeof useWorkstreamCreateContext> }) => {
  const { t } = useTranslation();
  const indicatorProducts = useSelectedIndicatorProducts();
  const selectedIds: string[] = form.watch(WORKSTREAM_FORM_KEYS.INDICATOR_PRODUCTS) ?? [];

  const toggleIndicatorProduct = (id: string) => {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    form.setValue(WORKSTREAM_FORM_KEYS.INDICATOR_PRODUCTS, next, { shouldDirty: true });
  };

  return (
    <Stack spacing={4} className='rounded-lg border border-divider p-6'>
      <Stack direction='row' centerMain className='justify-between'>
        <span className='typography-h3 text-text-primary'>{t('global.workstream.labels.indicatorProducts')}</span>
        <span className='typography-caption text-text-secondary'>
          {t('global.workstream.labels.productsAssigned', {
            selected: selectedIds.length,
            total: indicatorProducts.length,
          })}
        </span>
      </Stack>
      <Stack spacing={2}>
        {indicatorProducts.map((ip) => (
          <Stack
            key={ip.id}
            direction='row'
            centerMain
            className='justify-between rounded-lg border border-divider px-4 py-3'
          >
            <Stack direction='row' spacing={3} centerMain>
              <Checkbox checked={selectedIds.includes(ip.id)} onCheckedChange={() => toggleIndicatorProduct(ip.id)} />
              <span className='typography-body1 text-text-primary'>
                {ip?.widget?.widget_format?.overview?.title || ip.name}
              </span>
            </Stack>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
